import React, { useState, useEffect, useCallback } from 'react';
import { SlideGeneratorState, Slide, ApiSettings, ThemeFont, SlideBorderStyle } from './types';
import { ASPECT_RATIOS, DEFAULT_THEMES, TRANSLATIONS } from './constants';
import { AI_CONTENT_PROMPTS } from './constants/prompts';
import { generateSlidesWithUsage, generateSlideHTML } from './utils/slideGenerator';
import { ExportModal } from './components/ExportModal';
import { PresentationModal } from './components/PresentationModal';
import { Toast } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ApiErrorBoundary } from './components/ApiErrorBoundary';
import { getDecryptedApiKey } from './utils/encryption';
import { createDefaultBackgroundOptions, generatePicsumImage } from './utils/imageSearch';
import { processGeneratedSlides } from './utils/slidePostProcessor';
import { indexedDBService } from './utils/indexedDB';
import { processSlidesForSaving, processSlidesAfterLoading } from './utils/mediaStorage';
import { AspectRatioSelector } from './components/AspectRatioSelector';
import { SlidesContainer } from './components/SlidesContainer';
import { SlideRenderer } from './components/SlideRenderer';
import { SlideTypeSelector, SlideType } from './components/SlideTypeSelector';
import { LoadingAnimation } from './components/LoadingAnimation';
// import { ThemeFontSelector } from './components/ThemeFontSelector';  // Moved to SlideEditingPanel
// import { ThemeTemplateSelector } from './components/ThemeTemplateSelector';
// import { SlideBoxStyleSelector } from './components/SlideBoxStyleSelector';  // Moved to SlideEditingPanel
import { SlideCountSelector } from './components/SlideCountSelector';
import { SettingsModal } from './components/SettingsModal';
import { 
  Sparkles, 
  Loader,
  Presentation,
  Lightbulb,
  Settings
} from 'lucide-react';
import { useApiStore } from './stores/useApiStore';
// Hello World!
function App() {
  const [state, setState] = useState<SlideGeneratorState>({
    content: '',
    theme: DEFAULT_THEMES[0],
    aspectRatio: ASPECT_RATIOS[0],
    slides: [],
    activeSlideId: null,
    isGenerating: false,
    activeTab: 'preview',
    geminiApiKey: '',
    language: 'ko',
    selectedElementId: undefined,
  });

  // 다크모드 상태 추가
  const [isDarkMode, setIsDarkMode] = useState(false);
  // 내보내기 모달 상태
  const [showExportModal, setShowExportModal] = useState(false);
  // 프레젠테이션 모드 상태
  const [showPresentationMode, setShowPresentationMode] = useState(false);
  // 설정 모달 상태
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // 우측 콘텐츠 창 표시 상태 추가
  const [showRightPanel, setShowRightPanel] = useState(true);

  const [apiSettings, setApiSettings] = useState<ApiSettings>({
    geminiApiKey: '',
    isConfigured: false,
  });

  const [slideType, setSlideType] = useState<SlideType>('ppt');
  const [slideCount, setSlideCount] = useState<number>(5);
  const [themeFont, setThemeFont] = useState<ThemeFont>({
    id: 'modern-clean',
    name: '모던 클린',
    fontFamily: "'Pretendard', 'Noto Sans KR', sans-serif",
    fontUrl: 'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.8/dist/web/variable/pretendardvariable.css',
    effects: {
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textStroke: 'none',
      letterSpacing: '-0.02em',
      fontWeight: '600'
    }
  });
  // themeTemplate 상태 제거하고 기본값으로 대체
  // const [themeTemplate, setThemeTemplate] = useState<ThemeTemplateOption>({
  //   id: 'mixed-auto',
  //   name: 'mixedAuto',
  //   description: 'mixedAutoDesc',
  //   defaultLayout: 'title-top-content-bottom',
  //   icon: null
  // });
  const [slideBorderStyle, setSlideBorderStyle] = useState<SlideBorderStyle>({
    id: 'none',
    name: 'noStyle',
    borderWidth: 0,
    borderStyle: 'none',
    borderRadius: 0,
    boxShadow: 'none'
  });
  const [showToast, setShowToast] = useState<{ message: string, type: 'info' | 'success' | 'error' } | null>(null);
  const [draggedSlideIndex, setDraggedSlideIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [initializationError, setInitializationError] = useState<string | null>(null);

  // Navigation panel ref and scaling for slide previews
  // const navigationPanelRef = useRef<HTMLDivElement>(null); // Unused

  // Handle slide selection with proper scrolling
  const handleSlideSelection = useCallback((slideId: string) => {
    setState(prev => ({ ...prev, activeSlideId: slideId }));
    
    // Scroll to the selected slide
    setTimeout(() => {
      const slideElement = document.querySelector(`[data-slide-id="${slideId}"]`);
      if (slideElement) {
        try {
          const slideRect = slideElement.getBoundingClientRect();
          const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
          const targetScroll = currentScroll + slideRect.top - 100; // 100px offset from top for better visibility
          
          if (typeof targetScroll === 'number' && !isNaN(targetScroll)) {
            window.scrollTo({
              top: targetScroll,
              behavior: 'smooth'
            });
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error scrolling to slide:', error);
          }
        }
      }
    }, 100); // Small delay to ensure DOM updates
  }, []);

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedSlideIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedSlideIndex !== null && draggedSlideIndex !== dropIndex) {
      // 드래그한 슬라이드가 현재 활성 슬라이드인 경우, 이동 후에도 선택 상태 유지
      const draggedSlideId = state.slides[draggedSlideIndex]?.id;
      const wasActive = draggedSlideId === state.activeSlideId;
      
      handleSlideMove(draggedSlideIndex, dropIndex);
      
      // 이동 후에도 선택 상태 유지하고 자동 스크롤
      if (wasActive && draggedSlideId) {
        setTimeout(() => {
          setState(prev => ({ ...prev, activeSlideId: draggedSlideId }));
          
          // 이동된 슬라이드로 자동 스크롤
          setTimeout(() => {
            const slideElement = document.querySelector(`[data-slide-id="${draggedSlideId}"]`);
            if (slideElement) {
              try {
                const slideRect = slideElement.getBoundingClientRect();
                const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
                const targetScroll = currentScroll + slideRect.top - 100; // 100px offset from top for better visibility
                
                if (typeof targetScroll === 'number' && !isNaN(targetScroll)) {
                  window.scrollTo({
                    top: targetScroll,
                    behavior: 'smooth'
                  });
                }
              } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Error scrolling to moved slide:', error);
                }
              }
            }
          }, 100); // DOM 업데이트 후 스크롤
        }, 50);
      }
    }
    
    setDraggedSlideIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedSlideIndex(null);
    setDragOverIndex(null);
  };

  // Handle slide position changes
  const handleSlideMove = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= state.slides.length) {return;}
    
    setState(prev => {
      const newSlides = [...prev.slides];
      const [movedSlide] = newSlides.splice(fromIndex, 1);
      newSlides.splice(toIndex, 0, movedSlide);
      
      // Update slide orders
      newSlides.forEach((slide, index) => {
        slide.order = index + 1;
      });
      
      // IndexedDB에 실시간 저장 (blob URL을 base64로 변핒)
      processSlidesForSaving(newSlides).then(processedSlides => {
        indexedDBService.saveDraft(processedSlides).catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to save slides after move to IndexedDB:', error);
          }
        });
      });
      
      return { ...prev, slides: newSlides };
    });
  }, [state.slides.length]);

  // API 스토어 사용
  const { setGeminiApiKey } = useApiStore();
  
  // Load saved settings from localStorage and IndexedDB
  useEffect(() => {
    // Load quick settings from localStorage immediately (synchronous)
    const loadQuickSettings = () => {
      try {
        const savedApiKey = getDecryptedApiKey();
        const savedLanguage = localStorage.getItem('language') as 'ko' | 'en';
        const savedSlideType = localStorage.getItem('slideType') as SlideType;
        const savedDarkMode = localStorage.getItem('darkMode') === 'true';
      
      if (savedApiKey) {
        setApiSettings({
          geminiApiKey: savedApiKey,
          isConfigured: true,
        });
        setState(prev => ({ ...prev, geminiApiKey: savedApiKey }));
        // Zustand 스토어도 업데이트
        setGeminiApiKey(savedApiKey);
        
        // 디버깅을 위한 로그
        if (process.env.NODE_ENV === 'development') {
          console.log('[App] API 키 로드됨:', savedApiKey.substring(0, 10) + '...');
        }
      }
      
      if (savedLanguage) {
        setState(prev => ({ ...prev, language: savedLanguage }));
      }

      if (savedSlideType) {
        setSlideType(savedSlideType);
      }

      setIsDarkMode(savedDarkMode);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to load quick settings:', error);
        }
      }
    };

    // Load heavy data from IndexedDB asynchronously
    const initializeHeavyData = async () => {
      try {
        // Check if IndexedDB is supported
        if (!window.indexedDB) {
          throw new Error('IndexedDB is not supported in this browser');
        }
        
        // Initialize IndexedDB
        await indexedDBService.initDB();
        
        // Migrate data from localStorage if needed
        await indexedDBService.migrateFromLocalStorage();

        // Load slides from IndexedDB (large data)
        try {
          const savedSlides = await indexedDBService.loadDraft();
          if (savedSlides && savedSlides.length > 0) {
            // Convert base64 back to blob URLs for images/videos
            const processedSlides = processSlidesAfterLoading(savedSlides);
            setState(prev => ({ 
              ...prev, 
              slides: processedSlides,
              activeSlideId: processedSlides[0]?.id || null
            }));
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to restore slides from IndexedDB:', error);
          }
          
          // Try to load from backup if draft fails
          try {
            const backup = await indexedDBService.loadLatestAppStateBackup();
            if (backup && backup.slides.length > 0) {
              // Convert base64 back to blob URLs for images/videos
              const processedSlides = processSlidesAfterLoading(backup.slides);
              setState(prev => ({ 
                ...prev, 
                slides: processedSlides,
                activeSlideId: backup.activeSlideId || processedSlides[0]?.id || null
              }));
            }
          } catch (backupError) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Failed to restore from backup:', backupError);
            }
          }
        }
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';
        setInitializationError(errorMessage);
        
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to initialize heavy data:', error);
        }
        
        // Try to continue with localStorage fallback
        try {
          const legacySlides = localStorage.getItem('slides');
          if (legacySlides) {
            const parsedSlides = JSON.parse(legacySlides);
            if (Array.isArray(parsedSlides) && parsedSlides.length > 0) {
              setState(prev => ({ 
                ...prev, 
                slides: parsedSlides,
                activeSlideId: parsedSlides[0]?.id || null
              }));
            }
          }
        } catch (fallbackError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to load legacy data:', fallbackError);
          }
        }
      }
    };

    // Load quick settings immediately
    loadQuickSettings();
    
    // Load heavy data in the background
    initializeHeavyData();
  }, [setGeminiApiKey]);

  // API 키 상태 확인
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('App state geminiApiKey:', state.geminiApiKey);
      console.log('API settings geminiApiKey:', apiSettings.geminiApiKey);
    }
  }, [state.geminiApiKey, apiSettings.geminiApiKey]);

  // 다크모드 변경시 localStorage에 저장
  const toggleDarkMode = useCallback(() => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
  }, [isDarkMode]);

  // Update slides when theme or aspect ratio changes
  useEffect(() => {
    if (state.slides.length > 0) {
      setState(prev => ({
        ...prev,
        slides: prev.slides.map(slide => {
          // 화면 비율이 변경되면 배경 이미지도 새로 생성하지만 시드는 유지
          // 배경 타입에 따라 표시할 배경 결정
          const finalBackgroundImage = (slide.backgroundType === 'image' || !slide.backgroundType)
            ? (slide.backgroundSeed
                ? generatePicsumImage(
                    state.aspectRatio.width,
                    state.aspectRatio.height,
                    slide.backgroundSeed,
                    slide.backgroundBlur || 2,
                    slide.backgroundGrayscale || false
                  )
                : slide.backgroundImage)
            : undefined;

          // 템플릿이 변경되면 슬라이드 레이아웃도 업데이트
          const updatedLayout = 'title-top-content-bottom'; // 기본 레이아웃 사용

          // 모든 요소들을 테마에 맞게 업데이트 (기본 요소 특별 처리 제거)
          let updatedElements = slide.elements;
          // 슬라이드 테마가 설정된 경우 요소 업데이트 건너뛰기
          if (slide.elements && slide.elements.length > 0 && !slide.slideTheme) {
            updatedElements = slide.elements.map(element => {
              return {
                ...element,
                fontFamily: themeFont.fontFamily,
                // 텍스트/리스트 요소에 테마 색상 적용
                color: (element.type === 'text' || element.type === 'list') 
                  ? state.theme.secondary 
                  : (element.backgroundColor ? '#FFFFFF' : state.theme.secondary),
                leftBorderColor: element.leftBorderColor?.includes('#')
                  ? state.theme.secondary
                  : element.leftBorderColor || '',
                // 텍스트 요소에 verticalAlign이 없으면 'middle'로 설정
                verticalAlign: element.type === 'text' ? (element.verticalAlign || 'middle') : (element.verticalAlign || 'middle')
              };
            });
          }

          return {
            ...slide,
            backgroundImage: finalBackgroundImage,
            slideLayout: updatedLayout as Slide['slideLayout'],
            template: slide.template || 'title-top-content-bottom', // 기본 템플릿 사용
            elements: updatedElements,
            // 개별 슬라이드 테마가 설정되어 있으면 무조건 유지
            slideTheme: slide.slideTheme !== undefined ? slide.slideTheme : 'default',
            dividerType: slide.dividerType !== undefined ? slide.dividerType : 'straight',
            splitPosition: slide.splitPosition !== undefined ? slide.splitPosition : 30,
            contentBgColor: slide.contentBgColor || '#E5E7EB',
            htmlContent: generateSlideHTML(
              slide.title,
              slide.content,
              state.theme,
              state.aspectRatio,
              slide.order,
              themeFont,
              slideBorderStyle,
              updatedLayout,
              finalBackgroundImage,
              slide.backgroundBlur || 2,
              slide.themeOverlay || 0.3,
              slide.backgroundType === 'color' ? slide.backgroundColor : undefined, // 배경 타입 유지
              slide.backgroundPattern, // 패턴도 유지
              updatedElements, // 업데이트된 elements
              'ppt', // 기본값
              slide.fontBorder,
              slide.fontGlow,
              slide.slideTheme !== undefined ? slide.slideTheme : 'default',
              slide.dividerType !== undefined ? slide.dividerType : 'straight',
              slide.splitPosition !== undefined ? slide.splitPosition : 30
            ),
          };
        })
      }));
    }
  }, [state.theme, state.aspectRatio, state.slides.length, themeFont, slideBorderStyle]);

  const t = TRANSLATIONS[state.language];


  const handleSlideTypeChange = useCallback((type: SlideType) => {
    localStorage.setItem('slideType', type);
    setSlideType(type);
  }, []);

  // AI 콘텐츠 프롬프트 생성 함수
  const generateAIContentPrompt = useCallback(async () => {
    if (!state.geminiApiKey) {
      alert(t.apiKeyRequired);
      return;
    }

    setState(prev => ({ ...prev, isGenerating: true }));


    try {
      const prompts = AI_CONTENT_PROMPTS[state.language];
      const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
      
      setState(prev => ({ 
        ...prev, 
        content: randomPrompt,
        isGenerating: false 
      }));
    } catch {
      // Error handled without logging sensitive information
      setState(prev => ({ ...prev, isGenerating: false }));
    }
  }, [state.geminiApiKey, state.language, t.apiKeyRequired]);

  const handleGenerate = useCallback(async () => {
    if (!state.content.trim()) {return;}
    
    // API 키 확인 - state와 apiSettings 모두 확인
    const apiKey = state.geminiApiKey || apiSettings.geminiApiKey;
    if (!apiKey) {
      alert(t.apiKeyRequired);
      return;
    }

    setState(prev => ({ ...prev, isGenerating: true }));
    
    try {
      const result = await generateSlidesWithUsage(
        state.content, 
        state.theme, 
        state.aspectRatio, 
        apiKey,  // state.geminiApiKey 대신 apiKey 사용
        state.language,
        slideType,
        slideCount,
        'default', // 선택된 템플릿 사용
        'default',
        'straight'
      );
      
      // 토큰 사용량 토스트 메시지 표시
      if (result.tokenUsage) {
        const { promptTokens, outputTokens, totalTokens, estimatedCost } = result.tokenUsage;
        setShowToast({
          message: `토큰 사용량: 입력 ${promptTokens.toLocaleString()}, 출력 ${outputTokens.toLocaleString()}, 총 ${totalTokens.toLocaleString()} | 예상 비용: $${estimatedCost.toFixed(6)}`,
          type: 'info'
        });
      }
      
      // 슬라이드 생성 후 자동 처리 (규격 1번: 랜덤 테마 + 레이아웃 적용)
      if (process.env.NODE_ENV === 'development') {
        console.log('[슬라이드 생성] 후처리 시작:', result.slides.length, '개 슬라이드');
      }
      const processedSlides = await processGeneratedSlides(
        result.slides,
        state.aspectRatio,
        state.theme,
        themeFont,
        slideBorderStyle,
        false, // includeTitleInLayout - 기본값 false
        0.02   // layoutSpacing (normal spacing)
      );
      if (process.env.NODE_ENV === 'development') {
        console.log('[슬라이드 생성] 후처리 완료');
      }
      
      setState(prev => {
        const newState = {
          ...prev,
          slides: processedSlides,
          activeSlideId: processedSlides[0]?.id || null,
          isGenerating: false,
        };
        
        // IndexedDB에 저장 (blob URL을 base64로 변환)
        processSlidesForSaving(processedSlides).then(processedForSaving => {
          indexedDBService.saveDraft(processedForSaving).catch(error => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Failed to save slides to IndexedDB:', error);
            }
          });
        });
        
        return newState;
      });
    } catch (error) {
      // Error handled without logging sensitive information
      if (process.env.NODE_ENV === 'development') {
        console.error('Slide generation error:', error);
      }
      const errorMessage = error && typeof error === 'object' && 'userMessage' in error 
        ? error.userMessage 
        : 'Error generating slides. Please check your API key and try again.';
      alert(errorMessage);
      setState(prev => ({ ...prev, isGenerating: false }));
    }
  }, [state.content, state.geminiApiKey, apiSettings.geminiApiKey, state.theme, state.aspectRatio, 
      state.language, slideType, slideCount, themeFont, slideBorderStyle, t.apiKeyRequired]);

  const handleSlideUpdate = useCallback((updatedSlide: Slide) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('handleSlideUpdate called for slide:', updatedSlide.id, 'slideTheme:', updatedSlide.slideTheme);
    }
    setState(prev => {
      const newSlides = prev.slides.map(slide =>
        slide.id === updatedSlide.id ? updatedSlide : slide
      );
      
      // IndexedDB에 실시간 저장 (더 안정적인 저장, blob URL을 base64로 변환)
      processSlidesForSaving(newSlides).then(processedSlides => {
        indexedDBService.saveDraft(processedSlides).catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to save updated slides to IndexedDB:', error);
          }
          // 백업 저장 시도
          processSlidesForSaving(newSlides).then(processedForBackup => {
            indexedDBService.saveAppStateBackup(prev, processedForBackup, prev.activeSlideId).catch(backupError => {
              if (process.env.NODE_ENV === 'development') {
                console.warn('Failed to save backup:', backupError);
              }
            });
          });
        });
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('setState called with updated slides, slide', updatedSlide.id, 'theme:', updatedSlide.slideTheme);
      }
      return {
        ...prev,
        slides: newSlides,
      };
    });
  }, []);

  const handleSlideDelete = useCallback((slideId: string) => {
    setState(prev => {
      const newSlides = prev.slides.filter(slide => slide.id !== slideId);
      
      // IndexedDB에 실시간 저장 (blob URL을 base64로 변환)
      processSlidesForSaving(newSlides).then(processedSlides => {
        indexedDBService.saveDraft(processedSlides).catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to save slides after deletion to IndexedDB:', error);
          }
        });
      });
      
      return {
        ...prev,
        slides: newSlides,
        activeSlideId: prev.activeSlideId === slideId ? null : prev.activeSlideId,
      };
    });
  }, []);


  const handleAddSlide = useCallback((afterIndex?: number) => {
    const backgroundOptions = createDefaultBackgroundOptions(t.addContentHere);
    const backgroundImage = generatePicsumImage(
      state.aspectRatio.width,
      state.aspectRatio.height,
      backgroundOptions.seed,
      backgroundOptions.blur,
      backgroundOptions.grayscale
    );

    // 기본 요소 없이 완전히 빈 슬라이드 생성 (동적 레이아웃만 사용)

    const newSlide: Slide = {
      id: `slide-${Date.now()}`,
      title: t.newSlide,
      content: t.addContentHere,
      htmlContent: generateSlideHTML(
        t.newSlide, 
        t.addContentHere, 
        state.theme, 
        state.aspectRatio, 
        0,
        themeFont,
        slideBorderStyle,
        'title-center',
        backgroundImage,
        backgroundOptions.blur,
        0.3,
        undefined, // backgroundColor
        undefined, // backgroundPattern
        [], // 빈 요소 배열
        'ppt', // 기본값
        undefined, // fontBorder
        undefined, // fontGlow
        'default',
        'straight',
        30
      ),
      order: afterIndex !== undefined ? afterIndex + 1 : state.slides.length,
      template: 'title-center',
      backgroundImage,
      backgroundBlur: backgroundOptions.blur,
      backgroundSeed: backgroundOptions.seed,
      backgroundGrayscale: backgroundOptions.grayscale,
      slideLayout: 'title-top-content-bottom',
      themeOverlay: 0.3,
      elements: [],
      history: [],
      historyIndex: 0,
      backgroundColor: '#ffffff',
      backgroundType: 'image',
    };

    setState(prev => {
      const newSlides = afterIndex !== undefined 
        ? [
            ...prev.slides.slice(0, afterIndex + 1),
            newSlide,
            ...prev.slides.slice(afterIndex + 1)
          ]
        : [...prev.slides, newSlide];
      
      // IndexedDB에 실시간 저장 (blob URL을 base64로 변환)
      processSlidesForSaving(newSlides).then(processedSlides => {
        indexedDBService.saveDraft(processedSlides).catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to save slides after addition to IndexedDB:', error);
          }
        });
      });
      
      return {
        ...prev,
        slides: newSlides,
        activeSlideId: newSlide.id,
      };
    });
  }, [state.aspectRatio, state.theme, state.slides.length, themeFont, slideBorderStyle, t.addContentHere, t.newSlide]);

  // API 키 설정 핸들러
  const handleApiKeySave = useCallback((apiKey: string) => {
    setApiSettings({
      geminiApiKey: apiKey,
      isConfigured: true,
    });
    setState(prev => ({ ...prev, geminiApiKey: apiKey }));
    // Zustand 스토어도 업데이트
    setGeminiApiKey(apiKey);
  }, [setGeminiApiKey]);

  // Show initialization error if exists
  if (initializationError) {
    return (
      <div className={`h-screen flex items-center justify-center ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`text-center p-8 rounded-lg ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} shadow-lg max-w-md`}>
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">
            {state.language === 'ko' ? '초기화 오류' : 'Initialization Error'}
          </h2>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {state.language === 'ko' 
              ? '앱을 초기화하는 중 오류가 발생했습니다. 브라우저를 새로고침해주세요.' 
              : 'An error occurred while initializing the app. Please refresh the browser.'}
          </p>
          <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} font-mono`}>
            {initializationError}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {state.language === 'ko' ? '새로고침' : 'Refresh'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen transition-colors ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      {/* Removed header - content starts directly */}


      <div className="flex h-screen">
        {/* Left Panel - Slide Navigation - Hidden on mobile */}
        <div className={`w-48 flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r overflow-y-auto transition-colors hidden md:block`}>
          <div className="p-2">

            <h3 className={`text-sm font-semibold mb-1 px-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {state.language === 'ko' ? '슬라이드 탐색' : 'Slide Navigation'}
            </h3>
            <div className="flex items-center justify-between mb-3 px-2">
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {state.language === 'ko' ? `총 ${state.slides.length}개 슬라이드` : `Total ${state.slides.length} slides`}
              </div>
              <button
                onClick={() => setShowSettingsModal(true)}
                className={`p-1 rounded transition-all ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-50'
                }`}
                title={state.language === 'ko' ? '설정' : 'Settings'}
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
            {state.slides.length > 0 ? (
              <div className="space-y-2">
                {state.slides.map((slide, index) => (
                  <div
                    key={slide.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleSlideSelection(slide.id)}
                    className={`relative p-2 rounded-lg cursor-move transition-all duration-200 border-2 ${
                      state.activeSlideId === slide.id
                        ? isDarkMode 
                          ? 'border-blue-500 bg-blue-900/30 shadow-lg' 
                          : 'border-blue-500 bg-blue-50 shadow-lg'
                        : isDarkMode
                          ? 'border-gray-600 bg-gray-700 hover:bg-gray-600'
                          : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                    } ${
                      draggedSlideIndex === index ? 'opacity-50' : ''
                    } ${
                      dragOverIndex === index && draggedSlideIndex !== index ? 'border-yellow-500 bg-yellow-50/20' : ''
                    }`}
                  >
                    {/* Slide Preview */}
                    <div className="w-full relative">
                      <div 
                        className="relative bg-white rounded border overflow-hidden shadow-sm"
                        style={{
                          paddingBottom: `${(state.aspectRatio.height / state.aspectRatio.width) * 100}%`
                        }}
                      >
                        <div className="absolute inset-0">
                          <div 
                            style={{
                              width: '100%',
                              height: '100%',
                              transform: `scale(${161 / state.aspectRatio.width})`,
                              transformOrigin: 'top left'
                            }}
                          >
                            <SlideRenderer
                              slide={slide}
                              theme={state.theme}
                              aspectRatio={state.aspectRatio}
                              themeFont={themeFont}
                              borderStyle={slideBorderStyle}
                              isEditable={false}
                              scale={1}
                              containerWidth={state.aspectRatio.width}
                              containerHeight={state.aspectRatio.height}
                              language={state.language}
                              isDarkMode={isDarkMode}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Slide Info */}
                    <div className="mt-2 flex items-start gap-2">
                      <div className={`mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9h8M8 15h8" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className={`text-xs font-medium break-words ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {index + 1}. {slide.title}
                        </div>
                        <div className={`text-xs mt-1 opacity-70 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {slide.content.length > 50 ? slide.content.substring(0, 50) + '...' : slide.content}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <div className="text-xs">
                  {state.language === 'ko' ? '생성된 슬라이드가 없습니다' : 'No slides generated'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Center Panel - Slides with Direct WYSIWYG Editing */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden" id="main-content-area">
          <div className="pr-0" style={{ paddingLeft: '0px' }}>
            {state.slides.length > 0 ? (
            <ErrorBoundary>
              <SlidesContainer
              slides={state.slides}
              activeSlideId={state.activeSlideId}
              activeTab={state.activeTab}
              language={state.language}
              theme={state.theme}
              aspectRatio={state.aspectRatio}
              themeFont={themeFont}
              slideBorderStyle={slideBorderStyle}
              isDarkMode={isDarkMode}
              geminiApiKey={state.geminiApiKey}
              onTabChange={(tab) => setState(prev => ({ ...prev, activeTab: tab }))}
              onSlideSelect={(slideId) => setState(prev => ({ ...prev, activeSlideId: slideId }))}
              onSlideDelete={handleSlideDelete}
              onAddSlide={handleAddSlide}
              onSlideUpdate={handleSlideUpdate}
              onSlideMove={handleSlideMove}
              onThemeChange={(newTheme) => {
                // Update theme
                setState(prev => ({ ...prev, theme: newTheme }));
                
                // Update all slides with new theme colors - but preserve individual slide themes
                setState(prev => ({
                  ...prev,
                  slides: prev.slides.map(slide => {
                    // Only update element colors, not slide theme
                    const updatedElements = slide.elements?.map(element => {
                      if (element.type === 'text') {
                        // Check if it's a title based on position and font size
                        const isTitle = element.y && element.y <= 0.15 && element.fontSize && element.fontSize > 24;
                        return {
                          ...element,
                          backgroundColor: newTheme.accent + '20',
                          color: isTitle ? newTheme.secondary : newTheme.primary,
                          verticalAlign: 'middle' as const
                        };
                      } else if (element.type === 'chart') {
                        return {
                          ...element,
                          backgroundColor: newTheme.accent + '20'
                        };
                      }
                      return element;
                    });
                    
                    // Update slide with new elements
                    // HTML will be regenerated by SlideRenderer with new theme
                    // Keep individual slide theme unchanged
                    return {
                      ...slide,
                      elements: updatedElements || []
                    };
                  })
                }));
              }}
              onThemeFontChange={setThemeFont}
              onSlideBorderStyleChange={setSlideBorderStyle}
              onRightPanelToggle={() => setShowRightPanel(!showRightPanel)}
              isRightPanelOpen={showRightPanel}
            />
            </ErrorBoundary>
          ) : (
            <div className="flex-1 flex items-center justify-center min-h-screen">
              <div className="text-center">
                <Presentation className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">{t.noSlidesGenerated}</p>
                <p className="text-sm">{t.addContentAndGenerate}</p>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Right Panel - Input & Settings */}
        {showRightPanel && (
          <>
            {/* Desktop Right Panel */}
            <div className={`hidden md:block w-80 flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-l p-4 overflow-y-auto overflow-x-hidden transition-colors`}>
            <div className="space-y-4 max-w-full">
              <div className="w-full max-w-full overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {t.content}
                  </label>
                  <button
                    onClick={generateAIContentPrompt}
                    disabled={!state.geminiApiKey || state.isGenerating}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title={state.language === 'ko' ? '프롬프트 예시를 생성합니다' : 'Generate prompt example'}
                  >
                    {state.isGenerating ? (
                      <Loader className="w-3 h-3 animate-spin" />
                    ) : (
                      <Lightbulb className="w-3 h-3" />
                    )}
                    {state.language === 'ko' ? '프롬프트 예시' : 'Prompt Example'}
                  </button>
                </div>
                <textarea
                  value={state.content}
                  onChange={(e) => setState(prev => ({ ...prev, content: e.target.value }))}
                  placeholder={t.contentPlaceholder}
                  className={`w-full h-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>

              <ApiErrorBoundary>
                <button
                  onClick={handleGenerate}
                disabled={!state.content.trim() || state.isGenerating || !state.geminiApiKey}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg"
              >
                {state.isGenerating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    {t.generating}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    {t.generateSlides}
                  </>
                )}
              </button>
              </ApiErrorBoundary>

              <div className="w-full max-w-full overflow-hidden">
                <SlideTypeSelector
                  selectedType={slideType}
                  language={state.language}
                  onTypeChange={handleSlideTypeChange}
                  isDarkMode={isDarkMode}
                />
              </div>

              <div className="w-full max-w-full overflow-hidden flex items-center gap-2">
                <div className="flex-1">
                  <AspectRatioSelector
                    selectedRatio={state.aspectRatio}
                    onRatioChange={(aspectRatio) => setState(prev => {
                      // 기존 슬라이드들의 배경 이미지를 새로운 비율에 맞게 업데이트
                      const updatedSlides = prev.slides.map(slide => {
                        if (slide.backgroundSeed && slide.backgroundType === 'image') {
                          // 기존 배경 설정을 유지하면서 새로운 크기로 이미지 URL 업데이트
                          const newBackgroundImage = generatePicsumImage(
                            aspectRatio.width,
                            aspectRatio.height,
                            slide.backgroundSeed,
                            slide.backgroundBlur || 2,
                            slide.backgroundGrayscale || false
                          );
                          return {
                            ...slide,
                            backgroundImage: newBackgroundImage
                          };
                        }
                        return slide;
                      });
                      
                      return {
                        ...prev,
                        aspectRatio,
                        slides: updatedSlides
                      };
                    })}
                    language={state.language}
                    isDarkMode={isDarkMode}
                  />
                </div>
                {/* Mobile Settings Button - Only visible on mobile when sidebar is hidden */}
                <button
                  onClick={() => setShowSettingsModal(true)}
                  className={`p-2 rounded-lg transition-all md:hidden ${
                    isDarkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-50'
                  }`}
                  title={state.language === 'ko' ? '설정' : 'Settings'}
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>

              <div className="w-full max-w-full overflow-hidden">
                <SlideCountSelector
                  count={slideCount}
                  onCountChange={setSlideCount}
                  isDarkMode={isDarkMode}
                  language={state.language}
                />
              </div>

              {/* 폰트 스타일 선택기는 편집창으로 이동됨 */}
              {/* 
              <div className="w-full max-w-full overflow-hidden">
                <ThemeFontSelector
                  currentFont={themeFont}
                  onFontChange={setThemeFont}
                  isDarkMode={isDarkMode}
                  language={state.language}
                />
              </div>
              */}


            </div>
            </div>
            
            {/* Mobile Modal for Right Panel */}
            <div className="md:hidden fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50">
              <div className={`w-full max-w-lg max-h-[80vh] rounded-t-lg shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 overflow-y-auto`}>
                {/* Modal Header with Close Button */}
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {state.language === 'ko' ? '새 콘텐츠 생성' : 'Generate Content'}
                  </h3>
                  <button
                    onClick={() => setShowRightPanel(false)}
                    className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                  >
                    <span className="text-xl">×</span>
                  </button>
                </div>
                
                {/* Modal Content - Same as Desktop Panel */}
                <div className="space-y-4">
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-2">
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        {t.content}
                      </label>
                      <button
                        onClick={generateAIContentPrompt}
                        disabled={!state.geminiApiKey || state.isGenerating}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={state.language === 'ko' ? '프롬프트 예시를 생성합니다' : 'Generate prompt example'}
                      >
                        {state.isGenerating ? (
                          <Loader className="w-3 h-3 animate-spin" />
                        ) : (
                          <Lightbulb className="w-3 h-3" />
                        )}
                        {state.language === 'ko' ? '프롬프트 예시' : 'Prompt Example'}
                      </button>
                    </div>
                    <textarea
                      value={state.content}
                      onChange={(e) => setState(prev => ({ ...prev, content: e.target.value }))}
                      placeholder={t.contentPlaceholder}
                      className={`w-full h-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-colors ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                  </div>

                  <ApiErrorBoundary>
                    <button
                      onClick={handleGenerate}
                      disabled={!state.content.trim() || state.isGenerating || !state.geminiApiKey}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg"
                    >
                      {state.isGenerating ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          {t.generating}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          {t.generateSlides}
                        </>
                      )}
                    </button>
                  </ApiErrorBoundary>

                  <div className="w-full">
                    <SlideTypeSelector
                      selectedType={slideType}
                      language={state.language}
                      onTypeChange={handleSlideTypeChange}
                      isDarkMode={isDarkMode}
                    />
                  </div>

                  <div className="w-full">
                    <SlideCountSelector
                      count={slideCount}
                      onCountChange={setSlideCount}
                      isDarkMode={isDarkMode}
                      language={state.language}
                    />
                  </div>

                  <div className="w-full">
                    <AspectRatioSelector
                      selectedRatio={state.aspectRatio}
                      language={state.language}
                      onRatioChange={(ratio) => setState(prev => ({ ...prev, aspectRatio: ratio }))}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Loading Animation Overlay */}
      <LoadingAnimation 
        isVisible={state.isGenerating} 
        isDarkMode={isDarkMode}
      />

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          slides={state.slides}
          aspectRatio={state.aspectRatio}
          language={state.language}
          isDarkMode={isDarkMode}
          onClose={() => setShowExportModal(false)}
        />
      )}
      
      {/* Presentation Modal */}
      {showPresentationMode && (
        <PresentationModal
          slides={state.slides}
          aspectRatio={state.aspectRatio}
          theme={state.theme}
          themeFont={themeFont}
          slideBorderStyle={slideBorderStyle}
          language={state.language}
          isDarkMode={isDarkMode}
          onClose={() => setShowPresentationMode(false)}
        />
      )}
      
      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        language={state.language}
        isDarkMode={isDarkMode}
        onDarkModeToggle={toggleDarkMode}
        onLanguageChange={(lang) => setState(prev => ({ ...prev, language: lang }))}
        apiSettings={apiSettings}
        onApiSettingsChange={setApiSettings}
        onPresentationClick={() => setShowPresentationMode(true)}
        onExportClick={() => setShowExportModal(true)}
        hasSlides={state.slides.length > 0}
        onApiKeySave={handleApiKeySave}
      />
      
      {/* Toast 알림 */}
      {showToast && (
        <Toast
          message={showToast.message}
          type={showToast.type}
          onClose={() => setShowToast(null)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

export default App;